import { captureException } from '@sentry/nextjs';

import { Routes } from 'constants/routes.constants';
import { FeaturedItemFragment } from 'types/api/dato-operations.types';

// Define the allowed featured item types
type FeaturedItemType = 'event' | 'collection' | 'page';

/**
 * Get the slug from a featured item (event, collection, or page)
 */
export function getFeaturedItemSlug(
  featuredItem: FeaturedItemFragment,
): string | undefined {
  switch (featuredItem.featuredType) {
    case 'event':
      return featuredItem.event?.slug;
    case 'collection':
      return featuredItem.collection?.slug;
    case 'page':
      return featuredItem.page?.slug;
    default:
      captureException(new Error('Invalid featured item type for slug'), {
        tags: {
          service: 'featured-items',
          func: 'getFeaturedItemSlug',
          featuredType: featuredItem.featuredType,
        },
        extra: {
          featuredItemId: featuredItem.id,
          availableTypes: Object.keys(FeaturedItemRoutes),
        },
      });
      return undefined;
  }
}

/**
 * Get the title from a featured item (event, collection, or page)
 */
export function getFeaturedItemTitle(
  featuredItem: FeaturedItemFragment,
): string | undefined {
  switch (featuredItem.featuredType) {
    case 'event':
      return featuredItem.event?.title;
    case 'collection':
      return featuredItem.collection?.title;
    case 'page':
      return featuredItem.page?.title;
    default:
      captureException(new Error('Invalid featured item type for title'), {
        tags: {
          service: 'featured-items',
          func: 'getFeaturedItemTitle',
          featuredType: featuredItem.featuredType,
        },
        extra: {
          featuredItemId: featuredItem.id,
          availableTypes: Object.keys(FeaturedItemRoutes),
        },
      });
      return undefined;
  }
}

const FeaturedItemRoutes: Record<FeaturedItemType, string> = {
  event: Routes.EventDetail,
  collection: Routes.CollectionDetail,
  page: Routes.PageDetail,
};

export const getFeaturedItemRoute = (
  featuredItem: FeaturedItemFragment,
): string | undefined => {
  const route =
    FeaturedItemRoutes[featuredItem.featuredType as FeaturedItemType];

  if (!route) {
    captureException(new Error('Invalid featured item type'), {
      tags: {
        service: 'featured-items',
        func: 'getFeaturedItemRoute',
        featuredType: featuredItem.featuredType,
      },
      extra: {
        featuredItemId: featuredItem.id,
        availableTypes: Object.keys(FeaturedItemRoutes),
      },
    });
    return undefined;
  }

  return route;
};

/**
 * Filter featured items to only include valid ones
 * - Events: must exist and not be unlisted
 * - Collections: must exist and have upcoming events
 * - Pages: must exist
 */
export const filterValidFeaturedItems = (
  featuredItems: Array<FeaturedItemFragment>,
): Array<FeaturedItemFragment> => {
  return featuredItems.filter((featuredItem) => {
    switch (featuredItem.featuredType) {
      case 'event':
        return featuredItem.event && !featuredItem.event.unlistedEvent;
      case 'collection':
        return (
          featuredItem.collection &&
          featuredItem.collection.references.count > 0
        );
      case 'page':
        return !!featuredItem.page;
      default:
        captureException(new Error('Invalid featured item type in filter'), {
          tags: {
            service: 'featured-items',
            func: 'filterValidFeaturedItems',
            featuredType: featuredItem.featuredType,
          },
          extra: {
            featuredItemId: featuredItem.id,
            availableTypes: Object.keys(FeaturedItemRoutes),
          },
        });
        return false;
    }
  });
};
